import { useState } from "react";
import { Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

export const fmtAht = (s) => {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};
export const fmtNum = (n) => (n == null ? "—" : Number(n).toLocaleString());

export const filterQuery = (f) => {
  const p = new URLSearchParams();
  ["lob", "lang", "location", "month_from", "month_to"].forEach((k) => f?.[k] && p.set(k, f[k]));
  return p.toString();
};

export const heatStyle = (val, benchmark) => {
  if (val == null || benchmark == null || !benchmark) return {};
  const dev = Math.max(-0.25, Math.min(0.25, (val - benchmark) / benchmark)) / 0.25;
  const a = Math.abs(dev) * 0.55;
  return { background: dev <= 0 ? `rgba(16,185,129,${a})` : `rgba(244,63,94,${a})` };
};

export async function downloadPost(path, body, fallbackName, method = "post") {
  try {
    const res = method === "get"
      ? await api.get(path, { responseType: "blob", timeout: 300000 })
      : await api.post(path, body, { responseType: "blob", timeout: 300000 });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    const cd = res.headers["content-disposition"] || "";
    const m = cd.match(/filename="?([^";]+)"?/);
    a.download = m ? m[1] : fallbackName;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export ready");
  } catch (e) {
    if (e.response?.data instanceof Blob) {
      const txt = await e.response.data.text();
      try { toast.error(JSON.parse(txt).detail || "Export failed"); } catch { toast.error("Export failed"); }
    } else toast.error(apiError(e));
  }
}

export const KpiCard = ({ label, value, sub, color = "var(--c-cyan)", testId }) => (
  <div data-testid={testId} className="mk-card p-5">
    <p className="font-mono-hud text-[10px] uppercase tracking-[0.25em] text-thdim">{label}</p>
    <p className="font-mono-hud mt-2 text-2xl font-semibold" style={{ color }}>{value}</p>
    {sub && <p className="mt-1 text-xs text-thdim">{sub}</p>}
  </div>
);

const selCls = "rounded-md border border-thborder/10 bg-thinput px-2.5 py-2 text-xs text-thstrong focus:border-cyan-400 focus:outline-none";

export const FilterBar = ({ summary, filters, setFilters }) => {
  const set = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });
  const opt = (list) => (list || []).map((v) => <option key={v} value={v}>{v}</option>);
  return (
    <div data-testid="aht-filter-bar" className="mb-8 flex flex-wrap items-end gap-3">
      {[["lob", "LOB", summary.lobs], ["lang", "Language", summary.langs], ["location", "Location", summary.locations]].map(([k, label, list]) => (
        <div key={k}>
          <label className="font-mono-hud mb-1.5 block text-[9px] uppercase tracking-[0.2em] text-thdim">{label}</label>
          <select data-testid={`aht-filter-${k}`} value={filters[k]} onChange={set(k)} className={selCls}>
            <option value="">All</option>
            {opt(list)}
          </select>
        </div>
      ))}
      {summary.has_created && [["month_from", "From month"], ["month_to", "To month"]].map(([k, label]) => (
        <div key={k}>
          <label className="font-mono-hud mb-1.5 block text-[9px] uppercase tracking-[0.2em] text-thdim">{label}</label>
          <select data-testid={`aht-filter-${k}`} value={filters[k]} onChange={set(k)} className={selCls}>
            <option value="">Any</option>
            {opt(summary.months)}
          </select>
        </div>
      ))}
      {(filters.lob || filters.lang || filters.location || filters.month_from || filters.month_to) && (
        <button
          data-testid="aht-filter-clear"
          onClick={() => setFilters({ lob: "", lang: "", location: "", month_from: "", month_to: "" })}
          className="pb-2 text-xs text-thcyan hover:text-thcyan"
        >
          Clear filters
        </button>
      )}
    </div>
  );
};

export const DataTable = ({ columns, rows, testId }) => (
  <div className="overflow-x-auto rounded-xl border border-thborder/10 bg-thcard/60 backdrop-blur-xl">
    <table className="w-full text-left" data-testid={testId}>
      <thead>
        <tr className="border-b border-thborder/10">
          {columns.map((c) => (
            <th key={c.key} className="font-mono-hud whitespace-nowrap px-4 py-3 text-[9px] uppercase tracking-[0.2em] text-thdim">
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri} className="border-b border-thborder/5 hover:bg-thhover/5">
            {columns.map((c) => {
              const v = c.render ? c.render(row) : row[c.key];
              return (
                <td key={c.key} className="whitespace-nowrap px-4 py-2.5 text-sm text-thbody" style={c.cellStyle ? c.cellStyle(row) : {}}>
                  {v == null || v === "" ? "—" : v}
                </td>
              );
            })}
          </tr>
        ))}
        {rows.length === 0 && (
          <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-sm text-thfaint">No data for current filters</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

export const ExportButtons = ({ onExcel, onPpt, busyKey }) => (
  <div className="flex gap-3">
    {onExcel && (
      <button data-testid="aht-export-excel" onClick={onExcel} disabled={!!busyKey}
        className="flex items-center gap-2 rounded-md border border-[#10B981]/40 px-4 py-2 text-xs font-semibold text-[#10B981] transition-colors hover:bg-[#10B981]/10 disabled:opacity-40">
        {busyKey === "excel" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Excel
      </button>
    )}
    {onPpt && (
      <button data-testid="aht-export-ppt" onClick={onPpt} disabled={!!busyKey}
        className="flex items-center gap-2 rounded-md border border-[#3B82F6]/40 px-4 py-2 text-xs font-semibold text-[color:var(--c-purple)] transition-colors hover:bg-[#3B82F6]/10 disabled:opacity-40">
        {busyKey === "ppt" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} PowerPoint
      </button>
    )}
  </div>
);

export const SectionLoader = () => (
  <div className="flex items-center justify-center py-24 text-thcyan"><Loader2 className="h-7 w-7 animate-spin" /></div>
);

export const useBusy = () => useState(null);
